import {
    Activity,
    AlertTriangle,
    DollarSign,
    Frown,
    Loader2,
    Send,
    Smile,
    Target,
    TrendingDown,
    TrendingUp,
    Zap
} from "lucide-react";
import { useCallback, useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";
import ImageEditor from "./ImageEditor";

export const TAGS = {
  emotions: [
    { id: "zen", label: "Zen", icon: Smile, color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
    { id: "focused", label: "Focused", icon: Target, color: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
    { id: "frustrated", label: "Frustrated", icon: Frown, color: "bg-red-500/10 text-red-400 border-red-500/20" },
    { id: "anxious", label: "Anxious", icon: Activity, color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
    { id: "confident", label: "Confident", icon: Zap, color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  ],
  setups: [
    { id: "breakout", label: "Breakout", icon: TrendingUp, color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
    { id: "reversal", label: "Reversal", icon: TrendingDown, color: "bg-pink-500/10 text-pink-400 border-pink-500/20" },
    { id: "trend_following", label: "Trend Follow", icon: Activity, color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" },
  ],
  mistakes: [
    { id: "fomo", label: "FOMO", icon: AlertTriangle, color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    { id: "chasing", label: "Chasing", icon: AlertTriangle, color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
    { id: "revenge", label: "Revenge", icon: AlertTriangle, color: "bg-red-500/10 text-red-400 border-red-500/20" },
    { id: "oversizing", label: "Over-sizing", icon: AlertTriangle, color: "bg-red-500/10 text-red-400 border-red-500/20" },
    { id: "early_exit", label: "Early Exit", icon: AlertTriangle, color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  ]
};

const JournalQuickNote = ({ onSubmit, disabled, initialData = null, selectedDate }) => {
  const toast = useToast();
  const [note, setNote] = useState(initialData?.notes || "");
  const [entryPrice, setEntryPrice] = useState("");
  const [exitTarget, setExitTarget] = useState("");
  const [selectedTags, setSelectedTags] = useState({
    emotion: initialData?.emotionalState || null,
    setup: initialData?.tradeReason || null,
    mistakes: initialData?.mistakesMade ? initialData.mistakesMade.split(", ") : []
  });
  const [confidence, setConfidence] = useState(initialData?.confidenceLevel || 5);
  
  // Media State
  const [pastedImage, setPastedImage] = useState(null);
  const [attachments, setAttachments] = useState(initialData?.attachments || []);
  const [uploading, setUploading] = useState(false);

  // Parse initial note for Entry/Target if present (simple check)
  // Logic: If user is editing, we might want to extract "[Entry: 100 ...]" back to fields.
  // For now, keep it simple.

  const toggleMistake = (mistakeId) => {
    setSelectedTags(prev => ({
      ...prev,
      mistakes: prev.mistakes.includes(mistakeId)
        ? prev.mistakes.filter(id => id !== mistakeId)
        : [...prev.mistakes, mistakeId]
    }));
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        setPastedImage(blob);
        break; 
      }
    }
  }, []);

  const handleImageSave = async (blob) => {
    setPastedImage(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", blob, "chart_annotation.jpg");

      const { data } = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        setAttachments(prev => [...prev, {
          url: data.data.url,
          id: Date.now().toString(),
          type: "image"
        }]);
      }
    } catch (error) {
      console.error("Upload failed", error);
      toast.error("Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!note.trim() && !selectedTags.emotion && !selectedTags.setup) return;

    let finalNote = note;
    if (entryPrice || exitTarget) {
      const metrics = [];
      if (entryPrice) metrics.push(`Entry: ${entryPrice}`);
      if (exitTarget) metrics.push(`Target: ${exitTarget}`);
      finalNote = `[${metrics.join(" | ")}]\n${note}`;
    }

    onSubmit({
      notes: finalNote,
      emotionalState: selectedTags.emotion,
      tradeReason: selectedTags.setup,
      mistakesMade: selectedTags.mistakes.join(", "),
      confidenceLevel: confidence,
      attachments: attachments,
      entryType: "general", // Match DB constraint (trade/daily/weekly/monthly/general)
      entryDate: selectedDate || new Date().toISOString().split('T')[0]
    });

    if (!initialData) {
      setNote("");
      setEntryPrice("");
      setExitTarget("");
      setSelectedTags({ emotion: null, setup: null, mistakes: [] });
      setConfidence(5);
      setAttachments([]);
    }
  };

  return (
    <>
      <div className="relative">
        {/* Paste Overlay Hint */}
        {attachments.length === 0 && !note && (
          <div className="absolute top-14 right-4 pointer-events-none opacity-50 text-xs text-slate-500 hidden sm:block z-10">
            Tip: Paste (⌘+V) an image
          </div>
        )}

        <form onSubmit={handleSubmit} onPaste={handlePaste}>
          
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-slate-900/50 text-slate-200 rounded-lg p-3 border border-slate-700 focus:outline-none focus:border-indigo-500 mb-4 placeholder-slate-500 resize-none transition-all"
            rows="3"
            placeholder="What's on your mind?"
            disabled={disabled || uploading}
          />

          <div className="flex gap-4 mb-4">
             <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <DollarSign size={14} />
                </div>
                <input 
                  type="text"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  placeholder="Entry Price"
                  className="w-full bg-slate-900/50 text-slate-200 rounded-lg py-2 pl-8 pr-3 border border-slate-700 focus:outline-none focus:border-indigo-500 text-sm"
                />
             </div>
             <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <Target size={14} />
                </div>
                <input 
                  type="text"
                  value={exitTarget}
                  onChange={(e) => setExitTarget(e.target.value)}
                  placeholder="Exit Target"
                  className="w-full bg-slate-900/50 text-slate-200 rounded-lg py-2 pl-8 pr-3 border border-slate-700 focus:outline-none focus:border-indigo-500 text-sm"
                />
             </div>
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
               {attachments.map(att => (
                 <div key={att.id} className="relative group flex-shrink-0 h-20 w-32 bg-slate-900 rounded border border-slate-700 overflow-hidden">
                    <img src={att.url} alt="Attachment" className="h-full w-full object-cover" />
                    <button 
                      type="button"
                      onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMiIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxsaW5lIHgxPSIxOCIgeTE9IjYiIHgyPSI2IiB5Mj0iMTgiPjwvbGluZT48bGluZSB4MT0iNiIgeTE9IjYiIHgyPSIxOCIgeTI9IjE4Ij48L2xpbmU+PC9zdmc+" alt="Remove" />
                    </button>
                 </div>
               ))}
               {uploading && (
                 <div className="h-20 w-32 bg-slate-900 rounded border border-slate-700 border-dashed flex items-center justify-center">
                    <Loader2 className="animate-spin text-indigo-500" />
                 </div>
               )}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold text-slate-500 uppercase w-14">Mood</span>
                {TAGS.emotions.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => setSelectedTags(prev => ({ ...prev, emotion: prev.emotion === tag.id ? null : tag.id }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${selectedTags.emotion === tag.id ? `${tag.color} ring-1 ring-offset-1 ring-offset-slate-800 ring-slate-500` : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"}`}
                  >
                    <tag.icon size={12} />
                    {tag.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold text-slate-500 uppercase w-14">Setup</span>
                {TAGS.setups.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => setSelectedTags(prev => ({ ...prev, setup: prev.setup === tag.label ? null : tag.label }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${selectedTags.setup === tag.label ? `${tag.color} ring-1 ring-offset-1 ring-offset-slate-800 ring-slate-500` : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"}`}
                  >
                    <tag.icon size={12} />
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-slate-700/50 w-full" />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Confidence</span>
                <div className="flex-1 sm:w-48 group">
                  <input 
                    type="range" min="1" max="10" value={confidence}
                    onChange={(e) => setConfidence(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500"
                  />
                </div>
                <span className={`text-xs font-bold w-6 text-center ${confidence >= 8 ? "text-emerald-400" : confidence >= 5 ? "text-yellow-400" : "text-red-400"}`}>{confidence}</span>
              </div>

              <div className="flex gap-3 w-full sm:w-auto">
                <button
                  type="submit"
                  disabled={disabled || uploading || (!note.trim() && !selectedTags.emotion && !selectedTags.setup && attachments.length === 0)}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-lg font-medium transition-all shadow-lg flex items-center justify-center gap-2 text-sm"
                >
                  <Send size={16} />
                  Post Note
                </button>
              </div>
            </div>
          </div>
        </form>

        {pastedImage && (
          <ImageEditor 
            imageFile={pastedImage} 
            onSave={handleImageSave} 
            onCancel={() => setPastedImage(null)} 
          />
        )}
      </div>
    </>
  );
};

export default JournalQuickNote;
